/**
 * Settings Management API Service
 * This service handles communication with the Azure Functions backend for settings management.
 */

import axios from 'axios';

class SettingsService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE;
    console.log('SettingsService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get user settings
   * @param {string} userId - User ID
   * @returns {Promise} - API response with settings
   */
  async getSettings(userId) {
    try {
      const url = `${this.baseURL}/settings/get?userId=${userId}`;
      console.log(`Getting settings from: ${url}`);
      
      const response = await axios.get(url);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: 'Settings retrieved successfully' };
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  }

  /**
   * Create or update user settings
   * @param {Object} settingsData - Settings data
   * @returns {Promise} - API response
   */
  async saveSettings(settingsData) {
    try {
      console.log('Saving settings:', settingsData);
      
      const response = await axios.post(`${this.baseURL}/settings/post`, settingsData);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: 'Settings saved successfully' };
    } catch (error) {
      console.error('Error saving settings:', error);
      throw error;
    }
  }

  /**
   * Update user settings
   * @param {Object} settingsData - Settings data to update
   * @returns {Promise} - API response
   */
  async updateSettings(settingsData) {
    try {
      console.log('Updating settings:', settingsData);
      
      const response = await axios.put(`${this.baseURL}/settings/update`, settingsData);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: 'Settings updated successfully' };
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  /**
   * Export user settings
   * @param {string} userId - User ID
   * @returns {Promise} - API response with exported settings
   */
  async exportSettings(userId) {
    try {
      console.log('Exporting settings for user:', userId);
      
      const response = await axios.post(`${this.baseURL}/settings/export`, { userId });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: 'Settings exported successfully' };
    } catch (error) {
      console.error('Error exporting settings:', error);
      throw error;
    }
  }

  /**
   * Import user settings
   * @param {string} userId - User ID
   * @param {Object} settingsData - Settings data to import
   * @returns {Promise} - API response
   */
  async importSettings(userId, settingsData) {
    try {
      console.log('Importing settings for user:', userId);
      
      const response = await axios.post(`${this.baseURL}/settings/import`, { 
        userId, 
        settingsData 
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: 'Settings imported successfully' };
    } catch (error) {
      console.error('Error importing settings:', error);
      throw error;
    }
  }

  /**
   * Delete user settings
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async deleteSettings(userId) {
    try {
      console.log('Deleting settings for user:', userId);
      
      const response = await axios.delete(`${this.baseURL}/settings/delete`, {
        data: { userId }
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: 'Settings deleted successfully' };
    } catch (error) {
      console.error('Error deleting settings:', error);
      throw error;
    }
  }
}

// Export a singleton instance
const settingsService = new SettingsService();
export default settingsService;
